import { type ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { MarkdownEditor } from "@/components/ui/markdown-editor";
import { FormSection } from "@/components/ui/form-section";
import { CategoryChip } from "@/components/ui/category-chip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sparkles, Info, Clock, MapPin, Ticket, Globe } from "lucide-react";
import type { Category, Venue } from "@/types";
import type { EventFormValues } from "./eventFormValues";
import { RegistrationFields } from "./RegistrationFields";

interface Props {
  values: EventFormValues;
  onChange: (patch: Partial<EventFormValues>) => void;
  categories: Category[];
  venues: Venue[];
  /** Field names that were pre-filled by the AI agent (show a sparkle icon). */
  aiPreFilled?: Set<string>;
  /** Optional node rendered inside the basics section (e.g. AI location hint). */
  aboveDescription?: ReactNode;
  /** Optional node rendered after all sections (e.g. image manager). */
  afterFields?: ReactNode;
  /** Hide source/sourceUrl fields (e.g. AI approval auto-fills these). */
  hideSourceFields?: boolean;
  /** A single canonical link for AI proposals; existing API fields remain compatible. */
  unifiedEventLink?: boolean;
  /** Show the admin-only "Tylko podgląd admina" checkbox. Defaults to false. */
  showPreviewToggle?: boolean;
}

function AiBadge({ shown }: { shown: boolean }) {
  if (!shown) return null;
  return <Sparkles className="h-3 w-3 text-amber-500" aria-label="Sugestia AI" />;
}

export function EventFormFields({
  values,
  onChange,
  categories,
  venues,
  aiPreFilled,
  aboveDescription,
  afterFields,
  hideSourceFields,
  unifiedEventLink = false,
  showPreviewToggle,
}: Props) {
  const isPreFilled = (key: string) => aiPreFilled?.has(key) ?? false;
  const selectedCategory = categories.find((c) => c.id === values.categoryId);

  return (
    <div className="space-y-4">
      <FormSection icon={Info} title="Podstawowe informacje">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Tytuł</Label>
            <Input
              value={values.title}
              onChange={(e) => onChange({ title: e.target.value })}
              placeholder="np. Koncert w Kultowni"
              required
            />
          </div>

          {aboveDescription}

          <div className="space-y-2">
            <Label>Opis</Label>
            <MarkdownEditor
              value={values.eventDescription}
              onChange={(v) => onChange({ eventDescription: v })}
            />
          </div>
        </div>
      </FormSection>

      <FormSection icon={Clock} title="Termin">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="event-form-date">Data rozpoczęcia</Label>
            <Input
              id="event-form-date"
              type={values.isAllDay ? "date" : "datetime-local"}
              value={values.isAllDay ? values.date.slice(0, 10) : values.date}
              onChange={(e) => onChange({ date: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="event-form-endDate">Data zakończenia</Label>
            <Input
              id="event-form-endDate"
              type={values.isAllDay ? "date" : "datetime-local"}
              value={values.isAllDay ? values.endDate.slice(0, 10) : values.endDate}
              onChange={(e) => onChange({ endDate: e.target.value })}
            />
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <Checkbox
              id="event-form-isAllDay"
              checked={values.isAllDay}
              aria-describedby="event-form-date-help"
              onCheckedChange={(v) =>
                onChange({
                  isAllDay: !!v,
                  date: v ? values.date.slice(0, 10) : "",
                  endDate: v ? values.endDate.slice(0, 10) : "",
                })
              }
            />
            <Label htmlFor="event-form-isAllDay" className="cursor-pointer font-normal">
              Całodniowe
            </Label>
          </div>
          <p id="event-form-date-help" className="col-span-2 text-sm text-pulso-muted">
            Bez godzin — podaj samą datę lub zakres dat od–do, np. dla wystawy. Po wyłączeniu wpisz
            daty i godziny rozpoczęcia oraz opcjonalnego zakończenia.
          </p>
        </div>
      </FormSection>

      <FormSection icon={MapPin} title="Miejsce i kategoria">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-2">
            <Label className="flex items-center gap-1">
              Lokalizacja
              <AiBadge shown={isPreFilled("venueId")} />
            </Label>
            <Select value={values.venueId} onValueChange={(v) => onChange({ venueId: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Wybierz lokalizację" />
              </SelectTrigger>
              <SelectContent>
                {venues
                  .filter((v) => !v.isHidden)
                  .map((venue) => (
                    <SelectItem key={venue.id} value={venue.id}>
                      {venue.name}
                    </SelectItem>
                  ))}
                {(() => {
                  const current = venues.find((v) => v.id === values.venueId);
                  if (current?.isHidden) {
                    return (
                      <SelectItem key={current.id} value={current.id} disabled>
                        {current.name} (ukryta)
                      </SelectItem>
                    );
                  }
                  return null;
                })()}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              Kategoria
              <AiBadge shown={isPreFilled("categoryId")} />
            </Label>
            <Select value={values.categoryId} onValueChange={(v) => onChange({ categoryId: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Wybierz kategorię" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Podgląd</Label>
            <div className="flex h-9 items-center">
              {selectedCategory ? (
                <CategoryChip category={selectedCategory} withIcon />
              ) : (
                <span className="text-sm text-pulso-muted">Wybierz, aby zobaczyć</span>
              )}
            </div>
          </div>
        </div>
      </FormSection>

      {unifiedEventLink && (
        <FormSection icon={Globe} title="Strona wydarzenia" hint="opcjonalne">
          <Label htmlFor="event-form-page">Strona wydarzenia</Label>
          <Input
            id="event-form-page"
            value={values.detailsUrl}
            onChange={(e) => onChange({ detailsUrl: e.target.value, sourceUrl: e.target.value })}
            placeholder="https://..."
          />
        </FormSection>
      )}
      <FormSection icon={Ticket} title="Rejestracja i bilety">
        <div className="grid grid-cols-2 gap-4">
          {!unifiedEventLink && (
            <div className="space-y-2">
              <Label>Link do szczegółów</Label>
              <Input
                value={values.detailsUrl}
                onChange={(e) => onChange({ detailsUrl: e.target.value })}
                placeholder="https://..."
              />
            </div>
          )}
          <div className="col-span-2">
            <RegistrationFields
              registrationOptions={values.registrationOptions}
              registrationNotes={values.registrationNotes}
              onChange={onChange}
            />
          </div>
        </div>
      </FormSection>

      {!hideSourceFields && !unifiedEventLink && (
        <FormSection icon={Globe} title="Źródło" hint="opcjonalne">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nazwa źródła</Label>
              <Input value={values.source} onChange={(e) => onChange({ source: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>URL źródła</Label>
              <Input
                value={values.sourceUrl}
                onChange={(e) => onChange({ sourceUrl: e.target.value })}
              />
            </div>
          </div>
        </FormSection>
      )}

      {showPreviewToggle && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950">
          <Checkbox
            id="event-form-isPreview"
            checked={values.isPreview}
            onCheckedChange={(v) => onChange({ isPreview: !!v })}
            className="mt-0.5"
          />
          <div className="grid gap-1">
            <Label htmlFor="event-form-isPreview" className="cursor-pointer font-normal">
              Tylko podgląd admina
            </Label>
            <p className="text-xs text-muted-foreground">
              Widoczne tylko dla adminów. Zatwierdzenie nie wyśle powiadomień push.
            </p>
          </div>
        </div>
      )}

      {afterFields}
    </div>
  );
}
